import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query'
import { botsApi } from '../api/bots'
import { Bot } from '../types'

export const useBots = (ownerId?: string): UseQueryResult<Bot[], Error> => {
  return useQuery({
    queryKey: ['bots', ownerId],
    queryFn: () => {
      if (!ownerId) throw new Error('Owner ID is required')
      return botsApi.listBots(ownerId)
    },
    enabled: !!ownerId,
    staleTime: 3 * 60 * 1000, // 3 minutes
  })
}

export const useAccessibleBots = (enabled = true): UseQueryResult<Bot[], Error> => {
  return useQuery({
    queryKey: ['accessible-bots'],
    queryFn: botsApi.listBotsWithAccess,
    enabled,
    staleTime: 3 * 60 * 1000,
  })
}

export const useBot = (botId?: string): UseQueryResult<Bot, Error> => {
  return useQuery({
    queryKey: ['bot', botId],
    queryFn: () => {
      if (!botId) throw new Error('Bot ID is required')
      return botsApi.getBot(botId)
    },
    enabled: !!botId,
    staleTime: 3 * 60 * 1000,
  })
}

export const useCreateBot = (): UseMutationResult<Bot, Error, Parameters<typeof botsApi.createBot>[0]> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: botsApi.createBot,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['bots', data.owner_id] })
    },
  })
}

export const useUpdateBot = (): UseMutationResult<Bot, Error, { botId: string; data: Partial<Bot> }> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ botId, data }) => botsApi.updateBot(botId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['bot', data.id] })
      queryClient.invalidateQueries({ queryKey: ['bots', data.owner_id] })
    },
  })
}

export const useDeleteBot = (): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: botsApi.deleteBot,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bots'] })
    },
  })
}

export const useToggleBot = (): UseMutationResult<Bot, Error, string> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: botsApi.toggleBot,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['bot', data.id] })
      queryClient.invalidateQueries({ queryKey: ['bots', data.owner_id] })
    },
  })
}
export const useUpdateBotIntegration = (): UseMutationResult<Bot, Error, { botId: string; data: { flyer_key?: string | null; subgram_key?: string | null; tgrass_key?: string | null; gramads_key?: string | null } }> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ botId, data }) => botsApi.updateBotIntegration(botId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['bot', data.id] })
      queryClient.invalidateQueries({ queryKey: ['bots', data.owner_id] })
    },
  })
}
